import { useRef, useMemo, useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/joy';
import { localDateToString, localDateFromString, getWeekDays, getStartOfWeek } from '@/utils/datetime';
import { some } from 'lodash';
import { AddMealModal } from './AddMealModal';
import { useUpdateMealPlanEntryMutation } from '../api';
import { MealEntry } from '@/features/meals/components/MealEntry';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { useRecipesWithPersonalizedNutrition } from '@/features/recipes/hooks/useRecipesWithPersonalizedNutrition';
import { calculateDailyNutrition } from '../utils/dailyNutrition';
import { resolveMealPlanEntryItem } from '../utils/resolveMealPlanEntryItem';
import { DayMealsHeader } from '@/features/meals/components/DayMealsHeader';
import { MealPlanMealTypeSection } from './MealPlanMealTypeSection';
import { useGetProfileQuery } from '@/features/onboarding/api';
import { calculateDailyIntake } from '@/features/settings/utils/dailyIntake';
import { MealsWeeklyHeader } from '@/features/meals/components/MealsWeeklyHeader';
import { useDragAndDrop } from '@/features/meals/meal-plan/hooks/useDragAndDrop';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { MEAL_TYPES } from '@/features/meals/constants';

export function MobileMealPlanView({
  householdId,
  currentWeek,
  selectedDate,
  mealPlanEntries,
  recipes,
  ingredients,
  events,
  weekStartDay,
  onDateChange,
}) {
  const recipesWithPersonalizedNutrition = useRecipesWithPersonalizedNutrition(recipes);
  const weekDays = useMemo(() => getWeekDays(currentWeek), [currentWeek]);
  const currentWeekDays = useMemo(() => getWeekDays(getStartOfWeek(new Date(), weekStartDay)), [weekStartDay]);

  const [mealPlanEntriesCopy, setMealPlanEntriesCopy] = useState([]);
  const [updateMealPlanEntry] = useUpdateMealPlanEntryMutation();

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  const sectionIds = useMemo(() => weekDays.map(date => localDateToString(date)), [weekDays]);

  const { sectionRef } = useScrollToSection({
    sections: sectionIds,
    selectedSection: selectedDate,
    onSectionChange: (sectionId, options) => {
      onDateChange(localDateFromString(sectionId), options);
    },
    headerRef,
  });

  const isDateInCurrentWeek = (date) => {
    return some(currentWeekDays, (day) => day.toDateString() === date.toDateString());
  };

  const isDateEditable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isDateInCurrentWeek(date) || date >= today;
  };

  useEffect(() => {
    setMealPlanEntriesCopy(mealPlanEntries || []);
  }, [mealPlanEntries]);

  const { activeId, sensors, collisionDetection, handleDragStart, handleDragEnd, handleDragCancel } = useDragAndDrop({
    householdId,
    currentWeek,
    weekStartDay,
    mealPlanEntriesCopy,
    setMealPlanEntriesCopy,
    updateMealPlanEntry,
  });  

  const entriesByDate = useMemo(() => {
    const byDate = {};
    weekDays.forEach((date) => {
      byDate[localDateToString(date)] = [];
    });
    mealPlanEntriesCopy.forEach((entry) => {
      const entryDate = localDateToString(new Date(entry.date));
      if (byDate[entryDate]) {
        byDate[entryDate].push(entry);
      }
    });
    return byDate;
  }, [mealPlanEntriesCopy, weekDays]);

  const weekDaysWithMeals = useMemo(() => {
    return weekDays.map((date) => {
      const dateString = localDateToString(date);
      const entries = entriesByDate[dateString] || [];
      const mealsAssigned = MEAL_TYPES.map((type) => entries.some((e) => e.meal_type === type));
      return {
        date,
        mealsAssigned,
      };
    });
  }, [weekDays, entriesByDate]);

  // mealPlanEntriesCopy isn't scoped to the displayed week -- entriesByDate is
  // (only dates within weekDays get a key), so sum that instead.
  const weekMealCount = useMemo(
    () => Object.values(entriesByDate).reduce((sum, entries) => sum + entries.length, 0),
    [entriesByDate]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Box>
        <MealsWeeklyHeader
          ref={headerRef}
          currentWeek={currentWeek}
          weekDaysWithMeals={weekDaysWithMeals}
          selectedDate={selectedDate}
          onSelectDate={onDateChange}
          mealCount={weekMealCount}
          weekStartDay={weekStartDay}
        />

        <Stack gap={2} sx={{ pt: 0 }}>
          {weekDays.map((date) => {
            const dateString = localDateToString(date);
            return (
              <DayMeals
                key={dateString}
                isDragging={activeId != null}
                date={date}
                entries={entriesByDate[dateString] || []}
                recipes={recipesWithPersonalizedNutrition}
                ingredients={ingredients}
                events={events}
                householdId={householdId}
                isEditable={isDateEditable(date)}
                stickyTop={headerHeight}
                sectionRef={sectionRef(dateString)}
              />
            );
          })}
        </Stack>

        <NextWeekDropZone activeId={activeId} />
      </Box>

      {createPortal(
        <DragOverlay dropAnimation={null}>
          <DragOverlayContent
            activeId={activeId}
            mealPlanEntriesCopy={mealPlanEntriesCopy}
            recipes={recipesWithPersonalizedNutrition}
            ingredients={ingredients}
            events={events}
          />
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}

function NextWeekDropZone({ activeId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'next-week-drop-zone',
    data: { isNextWeekZone: true },
  });

  const isDragging = activeId?.startsWith('meal-');
  if (!isDragging) return null;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        width: '100%',
        minHeight: '60px',
        border: '2px dashed',
        borderColor: isOver ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-neutral-400)',
        borderRadius: 'md',
        backgroundColor: isOver ? 'var(--joy-palette-primary-50)' : 'var(--joy-palette-neutral-50)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        my: 2,
      }}
    >
      <Typography
        level="body-xs"
        sx={{
          color: 'var(--joy-palette-primary-500)',          
        }}
      >
        Drop here to move to next week
      </Typography>
    </Box>
  );
}

function DayMeals({ date, entries, recipes, ingredients, events, householdId, isEditable, sectionRef, stickyTop, isDragging }) {
  const [addMealOpen, setAddMealOpen] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('breakfast');
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const isToday = date.toDateString() === new Date().toDateString();

  const { data: profileData } = useGetProfileQuery();
  const profile = profileData?.data?.profile;
  const nutritionProfile = profile?.nutrition_profile;
  const dailyIntake = useMemo(() => calculateDailyIntake(nutritionProfile), [nutritionProfile]);
  
  const dailyNutrition = useMemo(() => {
    return calculateDailyNutrition(entries || [], recipes || [], dailyIntake);
  }, [entries, recipes, dailyIntake]);

  const handleAddMeal = (mealType) => {
    setSelectedMealType(mealType);
    setAddMealOpen(true);
  };

  return (
    <Box ref={sectionRef}>
      <DayMealsHeader
        date={date}
        dailyNutrition={dailyNutrition}
        stickyTop={stickyTop}
        mealCount={entries.length}
        isToday={isToday}
      />

      <Stack gap={1.75}>
        {MEAL_TYPES.map((mealType) => (
          <MealPlanMealTypeSection
            key={mealType}
            isDragging={isDragging}
            mealType={mealType}
            entries={entries}
            recipes={recipes}
            ingredients={ingredients}
            events={events}
            householdId={householdId}
            date={date}
            isEditable={isEditable}
            onAddMeal={handleAddMeal}
          />
        ))}
      </Stack>      

      {/* Mounted only while open. There's a modal instance per day, and each
          one filters and sorts the whole recipe list on mount -- keeping
          them all alive would repeat that work once per day on every load. */}
      {addMealOpen && <AddMealModal
        open
        onClose={() => setAddMealOpen(false)}
        date={date}
        dayName={dayName}
        dayNumber={date.getDate()}
        recipes={recipes}
        householdId={householdId}
        isEditable={isEditable}
        mealType={selectedMealType}
      />}
    </Box>
  );
}

function DragOverlayContent({ activeId, mealPlanEntriesCopy, recipes, ingredients, events }) {
  if (!activeId?.startsWith('meal-')) return null;

  const entryId = activeId.replace('meal-', '');
  const entry = mealPlanEntriesCopy?.find((e) => e.id === entryId);
  if (!entry) return null;

  const resolved = resolveMealPlanEntryItem(entry, { recipes, ingredients, events });
  if (!resolved) return null;

  return (
    <Box sx={{ width: 'calc(100vw - 32px)', maxWidth: 400, px: 0.5 }}>
      <MealEntry
        kind={resolved.kind}
        item={resolved.item}
        entry={entry}
        nutrition={resolved.item.personalizedNutrition}
        isSelected
      />
    </Box>
  );
}
